import { CbEvents, getSDK } from '@openim/wasm-client-sdk';

import { resetOpenImServerSyncState } from '../sync/serverSync';

export type OpenImEvent = {
  event: string;
  errCode: number;
  errMsg: string;
  data?: unknown;
  operationID: string;
};

export type OpenImLoginParams = {
  userID: string;
  token: string;
  platformID: number;
  apiAddr: string;
  wsAddr: string;
};

const CORE_WASM_PATH = 'openim/openIM.wasm';
// Full file path, not a directory: the SDK hands this straight to its SQLite build's `locateFile`.
// Pointing it at a directory makes that fetch hit the SPA's `index.html` fallback and fail with a
// `WebAssembly.instantiate(): expected magic word` compile error.
const SQL_WASM_PATH = 'openim/sql-wasm.wasm';

// The SDK's own `Emitter` is per-event-name, with no wildcard subscription, so the events the app
// consumes are forwarded explicitly. Anything added here must also be handled by a subscriber in
// `sync/realtimeListeners.ts` or `methods/uploadFile.ts` — those are the only two consumers.
const FORWARDED_EVENTS = [
  CbEvents.OnRecvNewMessage,
  CbEvents.OnNewRecvMessageRevoked,
  CbEvents.OnRecvMessageRevoked,
  CbEvents.OnConversationChanged,
  CbEvents.OnNewConversation,
  // Android waits on these before trusting getAllConversationList / SDK send (see
  // TgImOpenImBootstrap.onSyncServerFinish). Without them, Web refreshes an empty list and
  // createTextMessage/sendMessage hang or fail right after login.
  CbEvents.OnSyncServerStart,
  CbEvents.OnSyncServerFinish,
  CbEvents.OnSyncServerFailed,
  CbEvents.UploadComplete,
  // Group-join application lifecycle — refresh recipient + applicant lists without waiting for
  // the Contacts tab to re-open.
  CbEvents.OnGroupApplicationAdded,
  CbEvents.OnGroupApplicationDeleted,
  CbEvents.OnGroupApplicationAccepted,
  CbEvents.OnGroupApplicationRejected,
  // Mute / rights / member role — Android refreshes profile after these (TgImOpenImBootstrap).
  CbEvents.OnGroupInfoChanged,
  CbEvents.OnGroupMemberInfoChanged,
  CbEvents.OnGroupMemberAdded,
  CbEvents.OnGroupMemberDeleted,
  CbEvents.OnGroupDismissed,
  // Read receipts (Android/iOS both subscribe) -- without these, an outgoing message's checkmark
  // never advances from delivered to read once the peer actually opens the chat.
  CbEvents.OnRecvC2CReadReceipt,
  CbEvents.OnRecvGroupReadReceipt,
] as const;

// Raw Go exports the SDK publishes on `window` once its WASM core is running, plus the file-bridge
// helper its own `uploadFile` uses. `callOpenIm` below drives these directly rather than going
// through the SDK's typed wrappers, so the ~35 existing call sites keep their original raw
// `(operationID, ...args)` shape and return values.
type OpenImWindow = typeof window & {
  fileMapSet: (uuid: string, file: File) => void;
} & Record<string, (...args: unknown[]) => Promise<unknown>>;

const eventSubscribers = new Set<(evt: OpenImEvent) => void>();

let sdk: ReturnType<typeof getSDK> | undefined;
// Resolves once `loginOpenIm` has completed, which is also the point the SDK's WASM core has
// finished booting and published its raw Go exports on `window`. Every other OpenIM call in the app
// happens after login, so gating on this is what makes the direct `window[fnName]` calls safe.
let loginPromise: Promise<unknown> | undefined;
let wasmPreloadPromise: Promise<void> | undefined;

function getAssetUrl(relativePath: string) {
  // Resolved against the document location so the app's `base` config (e.g. a relative `./` or a
  // deployment subpath) is honoured, matching how the rest of the app's public assets resolve.
  return new URL(`${import.meta.env.BASE_URL}${relativePath}`, window.location.href).href;
}

/**
 * Warm the ~35MB OpenIM WASM + sql-wasm into the browser HTTP cache during phone/code entry so
 * `loginOpenIm` after signup doesn't stall on a cold download. Safe to call repeatedly; no-ops
 * once a preload or login is already in flight.
 */
export function preloadOpenImWasm(): void {
  if (wasmPreloadPromise || loginPromise) return;

  wasmPreloadPromise = Promise.all([
    fetch(getAssetUrl(CORE_WASM_PATH), { credentials: 'same-origin' }),
    fetch(getAssetUrl(SQL_WASM_PATH), { credentials: 'same-origin' }),
  ]).then(async ([core, sql]) => {
    // Drain bodies so the browser can keep them in HTTP cache; ignore failures — login still
    // retries the real fetch path.
    await Promise.allSettled([core.arrayBuffer(), sql.arrayBuffer()]);
  }).catch(() => undefined);
}

function ensureSdk() {
  if (sdk) return sdk;

  const instance = getSDK({
    coreWasmPath: getAssetUrl(CORE_WASM_PATH),
    sqlWasmPath: getAssetUrl(SQL_WASM_PATH),
  });

  FORWARDED_EVENTS.forEach((eventName) => {
    instance.on(eventName, (payload) => {
      eventSubscribers.forEach((cb) => cb(payload));
    });
  });

  sdk = instance;
  return instance;
}

export function loginOpenIm(params: OpenImLoginParams): Promise<unknown> {
  const instance = ensureSdk();
  // A re-login must not inherit the previous session's sync-finished latch.
  resetOpenImServerSyncState();
  loginPromise = instance.login(params);
  return loginPromise;
}

/** Drop the login latch so the next account can login after logout (iOS engine.logout pattern). */
export function resetOpenImLoginLatch(): void {
  loginPromise = undefined;
  resetOpenImServerSyncState();
}

export async function callOpenIm<T = unknown>(fnName: string, ...args: unknown[]): Promise<T> {
  ensureSdk();
  await loginPromise;

  const fn = (window as OpenImWindow)[fnName];
  if (typeof fn !== 'function') {
    throw new Error(`OpenIM WASM export not found: ${fnName}`);
  }

  return await fn(...args) as T;
}

export function subscribeOpenImEvents(cb: (evt: OpenImEvent) => void): () => void {
  eventSubscribers.add(cb);
  return () => eventSubscribers.delete(cb);
}

// Hands a `File` to the SDK's database worker ahead of an `uploadFile` call, keyed by the same
// `uuid` threaded through that call's request JSON — the WASM core reads the bytes back out through
// the worker's own `wasmOpen`/`wasmRead`/`wasmClose` bridge rather than touching the `File` itself.
// Mirrors what the SDK's own `uploadFile` wrapper does.
export function registerOpenImFile(uuid: string, file: File): void {
  (window as OpenImWindow).fileMapSet(uuid, file);
}
